import React, { useState, useEffect, useCallback } from 'react';
import QuizCard from './QuizCard';
import { useApp } from '../../context/AppContext';
import { Box, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { supabase } from '../../lib/supabase';

const QuizContainer = ({
  selectedSubject = 'all',
  sortOption = 'alphabet',
  onStart,
  onSubmit,
  onNext,
  timer,
  hasStarted,
  selectedTerm,
  setSelectedTerm,
  onSelectTerm,
  userInput,
  setUserInput,
}) => {
  const { terms, settings, wrongAnswers } = useApp();
  
  const [currentTermIndex, setCurrentTermIndex] = useState(0);
  const [currentTerm, setCurrentTerm] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [matchedKeywords, setMatchedKeywords] = useState([]);
  const [score, setScore] = useState(0);
  const [progress, setProgress] = useState(0);
  const [hintLevel, setHintLevel] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // 퀴즈 용어 목록 초기화
  const [quizTerms, setQuizTerms] = useState([]);
  
  const [openDialog, setOpenDialog] = useState(false);
  
  // 권종(학습권/정기권) 보유 여부 상태 추가
  const [canSubmit, setCanSubmit] = useState(false);
  
  // 권종 보유 여부 fetch (user_bll_passes 기준으로 수정)
  useEffect(() => {
    async function fetchBllPass() {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        setCanSubmit(false);
        return;
      }
      const { data, error } = await supabase
        .from('user_bll_passes')
        .select('type, count, expire_at')
        .eq('user_id', userId)
        .in('type', ['BLL_MONTHLY', 'BLL_6MONTH'])
        .gt('count', 0);

      if (error) {
        setCanSubmit(false);
        return;
      }
      // 만료일 체크 (정기권이 만료되지 않은 경우만 true)
      const now = new Date();
      const valid = (data || []).some(row => !row.expire_at || new Date(row.expire_at) > now);
      setCanSubmit(valid);
    }
    fetchBllPass();
  }, []);
  
  // 초기 로딩 시 용어 설정
  useEffect(() => {
    if (terms && terms.length > 0) {
      let filteredTerms = terms;
      if (selectedSubject && selectedSubject !== 'all') {
        if (selectedSubject === 'Must Know') {
          // Must Know 선택 시, is_must_know가 true/"true"/1인 term만 필터링
          filteredTerms = terms.filter(term => isMustKnow(term.is_must_know));
        } else {
          filteredTerms = terms.filter(
            term => term.subject && term.subject.trim() === selectedSubject.trim()
          );
        }
      }
      let sortedTerms = [...filteredTerms];
      if (sortOption === 'random') {
        sortedTerms = sortedTerms.sort(() => Math.random() - 0.5);
      } else if (sortOption === 'alphabet') {
        sortedTerms = sortedTerms.sort((a, b) => a.term.localeCompare(b.term, 'ko'));
      }
      setQuizTerms(sortedTerms);
      setCurrentTermIndex(0);
      setLoading(false);
    }
  }, [terms, selectedSubject, sortOption]);
  
  useEffect(() => {
  }, [currentTerm]);
  
  // 다음 문제로 이동
  const handleNext = useCallback(() => {
    if (currentTermIndex < quizTerms.length - 1) {
      setCurrentTermIndex(prevIndex => {
        const nextIndex = prevIndex + 1;
        setCurrentTerm(quizTerms[nextIndex]);
        return nextIndex;
      });
      
      setUserInput('');
      setShowAnswer(false);
      setMatchedKeywords([]);
      setScore(0);
      setProgress(0);
      setHintLevel(0);
      setShowHint(false);
      setHintPenalty(0);
    }
  }, [currentTermIndex, quizTerms]);
  
  // 이전 문제로 이동
  const handlePrevious = useCallback(() => {
    if (currentTermIndex > 0) {
      setCurrentTermIndex(prevIndex => {
        const nextIndex = prevIndex - 1;
        setCurrentTerm(quizTerms[nextIndex]);
        return nextIndex;
      });
      
      setUserInput('');
      setShowAnswer(false);
      setMatchedKeywords([]);
      setScore(0);
      setProgress(0);
      setHintLevel(0);
      setShowHint(false);
      setHintPenalty(0);
    }
  }, [currentTermIndex, quizTerms]);
  
  // 사용자 입력 처리
  const handleInputChange = (value) => {
    setUserInput(value);
    
    // 키워드 매칭 진행
    if (currentTerm && currentTerm.keywords) {
      const matched = [];
      currentTerm.keywords.forEach(keyword => {
        if (value.toLowerCase().includes(keyword.toLowerCase())) {
          matched.push(keyword);
        }
      });
      
      setMatchedKeywords(matched);
      
      // 진행도 계산
      if (currentTerm.keywords.length > 0) {
        const prog = (matched.length / currentTerm.keywords.length) * 100;
        setProgress(prog);
      }
    }
  };
  
  // 정답 제출
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;
    
    let finalScore = 0;
    
    if (currentTerm && currentTerm.keywords) {
      // 키워드 매칭 점수 계산 (80점 만점)
      if (currentTerm.keywords.length > 0) {
        finalScore = Math.round((matchedKeywords.length / currentTerm.keywords.length) * 80);
      }
      
      // 힌트 페널티 적용
      const penalty = hintLevel * 5; // 각 힌트 레벨마다 5점씩 감점
      setHintPenalty(penalty);
      finalScore = Math.max(0, finalScore - penalty);
      
      // 추가 점수: 정의가 비슷한 경우 (향후 구현 예정)
      finalScore = Math.min(100, finalScore + 20); // 현재는 임시로 20점 추가
    }
    
    setScore(finalScore);
    setShowAnswer(true);
    
    // 오답노트에 추가하는 로직도 여기에 추가 가능
  };
  
  // 힌트 표시
  const handleShowHint = () => {
    if (hintLevel < 4) { // 최대 4단계 힌트
      setHintLevel(prevLevel => prevLevel + 1);
      setShowHint(true);
    }
  };
  
  // quizTerms, currentTermIndex가 바뀔 때마다 currentTerm을 동기화
  useEffect(() => {
    if (quizTerms.length > 0) {
      setCurrentTerm(quizTerms[0]);
    }
  }, [quizTerms]);
  
  const handleSelectTerm = useCallback((term) => {
    const idx = quizTerms.findIndex(t => t.id === term.id);
    if (idx !== -1) {
      setCurrentTermIndex(idx);
      setCurrentTerm(term);
    } else {
      // 검색 결과가 quizTerms에 없을 때
      setQuizTerms([term]);
      setCurrentTermIndex(0);
      setCurrentTerm(term);
    }
  }, [quizTerms]);
  
  // selectedTerm이 바뀔 때마다 내부 currentTerm/currentTermIndex 동기화
  useEffect(() => {
    if (selectedTerm && quizTerms.length > 0) {
      const idx = quizTerms.findIndex(t => t.id === selectedTerm.id);
      if (idx !== -1) {
        setCurrentTermIndex(idx);
        setCurrentTerm(selectedTerm);
      }
    }
  }, [selectedTerm, quizTerms]);
  
  const handleTrySubmit = () => {
    if (!canSubmit) {
      setOpenDialog(true);
      return;
    }
    onSubmit();
  };
  
  console.log('QuizContainer canSubmit:', canSubmit);
  
  // is_must_know 값 타입을 느슨하게 체크하는 함수
  const isMustKnow = (val) => val === true || val === 'true' || val === 1;
  
  if (!terms || terms.length === 0) {
    // 데이터가 아직 로드되지 않았으면 로딩 스피너 표시
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // terms가 로드됐지만, 선택된 과목에 해당하는 용어가 없을 때만 "없음" 메시지 표시
  if (quizTerms.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: 300, justifyContent: 'center' }}>
        <img src="/no-terms.svg" alt="No terms" style={{ width: 80, marginBottom: 16 }} />
        <div style={{ fontWeight: 600, fontSize: 20, marginBottom: 8 }}>선택된 과목에 용어가 없습니다</div>
        <div style={{ color: '#888' }}>다른 과목을 선택하거나 오답노트를 확인해보세요.</div>
      </Box>
    );
  }
  
  return (
    <>
      <QuizCard
        quizTerms={quizTerms}
        currentTerm={currentTerm}
        setCurrentTerm={setCurrentTerm}
        selectedTerm={selectedTerm}
        setSelectedTerm={setSelectedTerm}
        onSelectTerm={handleSelectTerm}
        userInput={userInput}
        setUserInput={setUserInput}
        showAnswer={showAnswer}
        score={score}
        matchedKeywords={matchedKeywords}
        progress={progress}
        hintLevel={hintLevel}
        showHint={showHint}
        hintPenalty={hintPenalty}
        onInputChange={handleInputChange}
        onSubmit={handleSubmit}
        onShowHint={handleShowHint}
        onNext={onNext}
        onPrevious={handlePrevious}
        loading={loading}
        currentTermIndex={currentTermIndex}
        totalTerms={quizTerms.length}
        onStart={onStart}
        timer={timer}
        hasStarted={hasStarted}
        canSubmit={canSubmit}
      />
    </>
  );
};

export default QuizContainer;